import React, { useEffect } from "react";
import { Typography, Box } from "@mui/material";
import { SignUpData } from "Types";
import { CustomTextField, CustomButton, CustomCheckbox } from "Components/StyledComponents";
import { FormikProps } from "formik";
import * as yup from "yup";
import CountryCodeSelect from "./CountryCodeSelect";

export type SignUpFormProps = {
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
} & FormikProps<SignUpData>;

export const isPhoneNumber = (value: string | undefined) => {
  const codeSchema = yup.string().matches(
    /^\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
    "Invalid phone number"
  );
  return codeSchema.isValidSync(value || "");
};

export const isPhoneNumberWithCountryCode = (value: string | undefined) => {
  const codeSchema = yup.string().matches(
    /^(\+\d{1,3}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
    "Invalid phone number"
  );
  return codeSchema.isValidSync(value || "");
};

export const isEmail = (value: string | undefined) => {
  const emailSchema = yup.string().email();
  return emailSchema.isValidSync(value || "");
};

export const validateEmailOrPhone = (emailOrPhone: string | undefined) => {
  return isEmail(emailOrPhone) || isPhoneNumber(emailOrPhone);
};

// ✅ Standardized to countryAcronym (not countryacronym)
export const signUpFormSchema = yup.object().shape({
  name: yup.string().required("Name is a required field"),
  emailOrPhone: yup
    .string()
    .test("emailOrPhoneNumber", "Must be an email or a phone number", validateEmailOrPhone)
    .required("Email or code is a required field"),
  password: yup
    .string()
    .required("Password is a required field")
    .min(8, "Password must be at least 8 characters"),
  countryAcronym: yup.string(),
});

const SignUpForm: React.FC<SignUpFormProps> = (props: SignUpFormProps) => {
  const { handleChange, values, errors } = props;

  const [isPhone, setIsPhone] = React.useState(false);
  const [passwordType, setPasswordType] = React.useState<"password" | "text">("password");

  useEffect(() => {
    setIsPhone(isPhoneNumberWithCountryCode(values.emailOrPhone));
  }, [values.emailOrPhone]);

  return (
    <Box className="signup-form-container">
      <CustomTextField
        id="name"
        name="name"
        type="text"
        onChange={handleChange}
        label="Full Name"
        value={values.name || ""}
        error={!!errors.name}
        helperText={errors.name}
        autoComplete="name"
        inputProps={{
          autoCorrect: "off",
          spellCheck: false,
        }}
        fullWidth
      />

      <Box
        sx={{
          minWidth: 310,
          display: "flex",
          flexDirection: "row",
        }}
      >
        {isPhone && (
          <CountryCodeSelect
            handleChange={handleChange}
            countryAcronym={(values as any).countryAcronym}
          />
        )}

        <CustomTextField
          onChange={handleChange}
          id="emailOrPhone"
          name="emailOrPhone"
          type={isPhone ? "tel" : "email"}
          inputMode={isPhone ? "tel" : "email"}
          autoComplete={isPhone ? "tel" : "email"}
          inputProps={{
            autoCapitalize: "none",
            autoCorrect: "off",
            spellCheck: false,
          }}
          label="Email or Phone Number"
          value={values.emailOrPhone || ""}
          error={!!errors.emailOrPhone}
          helperText={errors.emailOrPhone}
          fullWidth
        />
      </Box>

      <CustomTextField
        onChange={handleChange}
        id="password"
        name="password"
        type={passwordType}
        label="Password"
        value={values.password || ""}
        error={!!errors.password}
        helperText={errors.password}
        autoComplete="new-password"
        inputProps={{
          autoCapitalize: "none",
          autoCorrect: "off",
          spellCheck: false,
        }}
        fullWidth
      />

      <CustomCheckbox
        id="showPassword"
        name="showPassword"
        label="Show password"
        value={false}
        checked={false}
        defaultChecked={false}
        sx={{
          margin: 0
        }}
        onChange={()=>{
          if(passwordType === 'password') {
            setPasswordType('text')
          } else if(passwordType === 'text') {
            setPasswordType('password')
          }
        }}
      />

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <CustomButton variant="contained" type="submit">
          CREATE ACCOUNT
        </CustomButton>
      </Box>

      <Box>
        {errors.unknown && (
          <Typography color="error" variant="h6">
            {errors.unknown}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default SignUpForm;